/**
 * Per-provider API key rows. Only providers referenced by at least one
 * LlmConfig are shown — there's no point asking the user for a DeepSeek
 * key if they don't have any DeepSeek config.
 */
import { useState } from 'react'
import { PROVIDERS, type ProviderId } from '@/data/llmCatalog'
import { testProviderKey } from '@/api/settings'
import type { ProviderKeys } from '@/types'
import { CheckCircle2, XCircle, Eye, EyeOff } from 'lucide-react'
import toast from 'react-hot-toast'

interface Props {
  /** Providers that need a key (filtered by referenced configs). */
  providers: ProviderId[]
  /** Masked: server returns booleans showing which keys are set. */
  masked: Partial<Record<keyof ProviderKeys, boolean>>
  /** Working draft (plaintext inputs). Empty string means "keep stored value". */
  draft: ProviderKeys
  onChange: (next: ProviderKeys) => void
}

const PROVIDER_KEY_FIELD: Record<ProviderId, keyof ProviderKeys> = {
  openai: 'openai_api_key',
  anthropic: 'anthropic_api_key',
  google: 'google_api_key',
  deepseek: 'deepseek_api_key',
}

export default function ProviderKeyPanel({ providers, masked, draft, onChange }: Props) {
  if (providers.length === 0) {
    return (
      <div style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>
        No providers in use yet. Add an LLM configuration above and the keys for
        the chosen providers will appear here.
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {providers.map((p) => {
        const spec = PROVIDERS.find((x) => x.id === p)!
        const field = PROVIDER_KEY_FIELD[p]
        return (
          <KeyRow
            key={p}
            providerId={p}
            label={spec.label}
            envHint={spec.api_key_env}
            isSet={!!masked[field]}
            value={draft[field] || ''}
            onChange={(v) => onChange({ ...draft, [field]: v })}
          />
        )
      })}
    </div>
  )
}

function KeyRow({
  providerId,
  label,
  envHint,
  isSet,
  value,
  onChange,
}: {
  providerId: ProviderId
  label: string
  envHint: string
  isSet: boolean
  value: string
  onChange: (v: string) => void
}) {
  const [reveal, setReveal] = useState(false)
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<{ ok: boolean; error: string | null } | null>(null)

  async function handleTest() {
    if (!value && !isSet) {
      toast.error('Enter a key to test')
      return
    }
    if (!value) {
      // No new value — nothing to test in this session (server has the
      // stored one but the test endpoint takes a plaintext key, not the
      // stored hash). Tell the user to re-enter to test.
      toast('Enter the key again to test', { icon: 'ℹ️' })
      return
    }
    setTesting(true)
    setTestResult(null)
    try {
      const result = await testProviderKey(providerId, value)
      setTestResult(result)
      if (result.ok) toast.success(`${label} key works`)
      else toast.error(result.error || 'Key test failed')
    } catch (e: any) {
      const err = e?.response?.data?.detail || e?.message || 'Test failed'
      setTestResult({ ok: false, error: err })
      toast.error(err)
    } finally {
      setTesting(false)
    }
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '160px 1fr auto auto',
        gap: 10,
        alignItems: 'center',
        padding: '10px 12px',
        border: '1px solid var(--color-border)',
        borderRadius: 8,
        background: 'var(--color-bg-elevated)',
      }}
    >
      <div>
        <div style={{ fontWeight: 600, fontSize: 14 }}>{label}</div>
        <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{envHint}</div>
      </div>
      <input
        type={reveal ? 'text' : 'password'}
        className="input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={isSet ? '••••••••  (key already stored — leave blank to keep)' : 'sk-…'}
      />
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        onClick={() => setReveal((r) => !r)}
        title={reveal ? 'Hide' : 'Reveal'}
        style={{ padding: '4px 8px' }}
      >
        {reveal ? <EyeOff size={14} /> : <Eye size={14} />}
      </button>
      <button
        type="button"
        className="btn btn-secondary btn-sm"
        onClick={handleTest}
        disabled={testing}
        style={{ minWidth: 70 }}
      >
        {testing ? 'Testing…' : 'Test'}
      </button>
      {testResult && (
        <div
          style={{
            gridColumn: '1 / -1',
            fontSize: 13,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            color: testResult.ok ? 'var(--color-success, #16a34a)' : 'var(--color-danger, #dc2626)',
          }}
        >
          {testResult.ok ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
          {testResult.ok ? 'Key validated.' : testResult.error}
        </div>
      )}
    </div>
  )
}
